import React from 'react';
import { Building2, Home, PaintBucket, Droplet, Shield, Wind, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';

const iconMap = {
  Building2: Building2,
  Home: Home,
  PaintBucket: PaintBucket,
  Droplet: Droplet,
  Shield: Shield,
  Wind: Wind
};

const Services = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1733431772808-82d878e59000?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxkcnl3YWxsJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc3NDQ4Mjc2Mnww&ixlib=rb-4.1.0&q=85"
            alt="Services Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Comprehensive drywall and insulation solutions tailored to your project needs
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="border border-gray-200 hover:border-[#00AEEF] transition-all duration-300 hover:shadow-2xl group">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00AEEF] transition-all duration-300">
                      <IconComponent className="w-8 h-8 text-[#00AEEF] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                        <span>Professional installation</span>
                      </li>
                      <li className="flex items-start gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                        <span>Quality materials</span>
                      </li>
                      <li className="flex items-start gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                        <span>Competitive pricing</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Images Gallery */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Our Work in Action</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See the quality and precision we bring to every project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="relative h-80 rounded-xl overflow-hidden shadow-xl group">
              <img 
                src="https://images.pexels.com/photos/6474207/pexels-photo-6474207.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Commercial Project"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Commercial Projects</h3>
                <p className="text-gray-200">Large-scale commercial installations</p>
              </div>
            </div>

            <div className="relative h-80 rounded-xl overflow-hidden shadow-xl group">
              <img 
                src="https://images.pexels.com/photos/6474313/pexels-photo-6474313.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Residential Project"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Residential Projects</h3>
                <p className="text-gray-200">Beautiful home renovations & builds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose Sat Drywall</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference of working with true professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { title: 'Expert Craftsmanship', desc: 'Highly skilled professionals with years of experience' },
              { title: 'Quality Materials', desc: 'We use only premium, industry-leading materials' },
              { title: 'On-Time Delivery', desc: 'Projects completed on schedule, every time' },
              { title: 'Competitive Pricing', desc: 'Fair, transparent pricing with no hidden costs' },
              { title: 'Licensed & Insured', desc: 'Fully certified and insured for your peace of mind' },
              { title: 'Customer Satisfaction', desc: '100% satisfaction guarantee on all projects' }
            ].map((item, index) => (
              <div key={index} className="flex gap-4 items-start p-6 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-[#00AEEF]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#00AEEF] to-[#0096d1] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Need Our Services?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Contact us today for a free consultation and quote
          </p>
          <Button 
            onClick={() => navigate('/contact')}
            className="bg-white text-[#00AEEF] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-xl"
          >
            Get Free Quote
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
